import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { distributionListService } from "../shared/api/distributionListService";
import { IDistributionListIndexResource } from "../shared/types";
import { DistributionListCreateDrawer, DistributionListUpdateDrawer } from "./DistributionListDrawer";

export const DistributionListList = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const crudOperations = useLaravelCrudEntity(distributionListService, {
		entityName: t("Distribution list"),
		messages: {
			create: t("Distribution list was saved."),
			update: t("Distribution list was saved."),
			delete: t("Distribution list was deleted."),
		},
	});
	const canCreate = useHasPermission<PermissionName>(["distributionList.create"]);
	const canUpdate = useHasPermission<PermissionName>(["distributionList.update"]);
	const dataListColumns: IDrawerDataListColumn<IDistributionListIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Description"),
				valueKey: "description",
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit distribution list")}
							label={t("Edit distribution list")}
							action={openDrawer}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuLinkItem
							to={`${id}`}
							title={t("View distribution list")}
							label={t("View distribution list")}
							icon="eye"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={() => crudOperations.deleteEntity(id)}
							title={t("Delete distribution list")}
							label={t("Delete distribution list")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this distribution list?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[crudOperations, t],
	);

	return (
		<PaginatedDrawerAutoDataList
			canCreate={canCreate}
			canUpdate={canUpdate}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(distributionListService.listKey, distributionListService.index)}
			renderCreateDrawer={(drawerProps) => (
				<DistributionListCreateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.createEntity(entityFormState)}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<DistributionListUpdateDrawer
					{...drawerProps}
					onSubmit={(entityFormState) => crudOperations.updateEntity(drawerProps.id, entityFormState)}
				/>
			)}
			translations={{
				title: t("Distribution lists"),
				empty: t("No distribution list available."),
			}}
			onRowClick={({ id }) => navigate(`${id}`)}
		/>
	);
};
